import { SpatialHash2D } from "../../../sim";
import { U, C, roleStats } from "./accessors";
import type {
  InfiniteWarState,
  IwCardId,
  IwCardState,
  IwRoleId,
  IwTeam,
  IwTileOwner,
  IwUnit,
} from "./types";

export function createCardState(deck: IwCardId[]): IwCardState {
  const handMax = U.mana.handMax;
  if (deck.length < handMax) {
    throw new Error(`Infinite War deck needs ≥${handMax} cards, got ${deck.length}`);
  }
  return {
    hand: deck.slice(0, handMax),
    cycle: deck.slice(handMax),
  };
}

/** Played card leaves its hand slot; next cycle card fills that slot; played goes to cycle end. */
export function cycleAfterPlay(cards: IwCardState, card: IwCardId): void {
  const idx = cards.hand.indexOf(card);
  if (idx < 0) return;
  const next = cards.cycle.shift();
  if (next === undefined) {
    cards.hand.splice(idx, 1);
    cards.cycle.push(card);
    return;
  }
  cards.hand[idx] = next;
  cards.cycle.push(card);
}

export function createNeutralTileOwners(): IwTileOwner[][] {
  return Array.from({ length: U.field.rows }, () =>
    Array.from({ length: U.field.cols }, () => null),
  );
}

/** Build match state + starting line. Caller must run `updateTileOwners` once. */
export function createInfiniteWarState(): InfiniteWarState {
  const southGod = U.demigod.south;
  const northGod = U.demigod.north;
  const southRole = southGod.roleId as IwRoleId;
  const northRole = northGod.roleId as IwRoleId;
  const state: InfiniteWarState = {
    time: 0,
    nextId: 1,
    nextWaveId: 1,
    nextBlastId: 1,
    units: [],
    waves: [],
    pendingBlasts: [],
    winner: null,
    mana: { south: U.mana.start, north: U.mana.start },
    manaCap: U.mana.capStart,
    aiThink: 2.2,
    lastAiDecision: { action: "hold" },
    conversions: { south: 0, north: 0 },
    events: [],
    cards: {
      south: createCardState([...(C.decks.south as IwCardId[])]),
      north: createCardState([...(C.decks.north as IwCardId[])]),
    },
    demigods: {
      south: {
        team: "south",
        roleId: southRole,
        x: southGod.x,
        y: southGod.y,
        hp: roleStats(southRole).hp,
        maxHp: roleStats(southRole).hp,
        attackCooldown: 0.9,
      },
      north: {
        team: "north",
        roleId: northRole,
        x: northGod.x,
        y: northGod.y,
        hp: roleStats(northRole).hp,
        maxHp: roleStats(northRole).hp,
        attackCooldown: 0.9,
      },
    },
    tileOwners: createNeutralTileOwners(),
    crowdHash: new SpatialHash2D<IwUnit>(),
  };

  U.startingLine.south.forEach((roleId, i) =>
    spawnUnit(state, "south", roleId as IwRoleId, i),
  );
  U.startingLine.north.forEach((roleId, i) =>
    spawnUnit(state, "north", roleId as IwRoleId, i),
  );
  return state;
}

export type SpawnAnchor = { x: number; y: number };

export function spawnUnit(
  state: InfiniteWarState,
  team: IwTeam,
  roleId: IwRoleId,
  seed: number,
  offsetX = 0,
  offsetY = 0,
  anchor?: SpawnAnchor,
): void {
  const stats = roleStats(roleId);
  if (stats.tags.includes("demigod")) return;
  const south = team === "south";
  let x: number;
  let y: number;
  if (anchor) {
    x = clampToField(anchor.x + offsetX);
    y = clampToFieldY(anchor.y + offsetY);
  } else {
    const lane = (seed * 47) % 390;
    const row = Math.floor((seed * 17) % 3) * 18;
    x = 75 + lane + offsetX;
    y = (south ? 720 : 230) + (south ? row : -row) + offsetY;
  }
  state.units.push({
    id: state.nextId++,
    team,
    roleId,
    x,
    y,
    hp: stats.hp,
    maxHp: stats.hp,
    attackCooldown: 0.25 + (seed % 5) * 0.07,
    intentVx: 0,
    intentVy: 0,
    facingX: 0,
    facingY: south ? -1 : 1,
    phase: "alive",
    killerTeam: null,
    soulRemain: 0,
    blight: 0,
    buffMul: 1,
    engageTargetId: null,
  });
}

function clampToField(x: number): number {
  const margin = 24;
  return Math.min(U.field.x + U.field.w - margin, Math.max(U.field.x + margin, x));
}

function clampToFieldY(y: number): number {
  const margin = 24;
  return Math.min(U.field.y + U.field.h - margin, Math.max(U.field.y + margin, y));
}

export function drainEvents(state: InfiniteWarState) {
  const events = state.events;
  state.events = [];
  return events;
}

export function teamCount(state: InfiniteWarState, team: IwTeam): number {
  return state.units.filter((u) => u.phase === "alive" && u.team === team).length;
}

export function totalSoulValue(state: InfiniteWarState): number {
  return state.units.reduce((sum, unit) => sum + roleStats(unit.roleId).soulValue, 0);
}
